import { useCallback, useEffect, useRef, useState } from "react"
import axios from "axios"
import { getAuth } from "firebase/auth"

//GraphQl
import { useLazyQuery } from "@apollo/client"
import { GET_MICROSERVICE_TOKENS } from "./queries"

//Helpers
import { LogError } from "../../helpers/logger" // Server URL
import serverUrl from "../../config/serverUrl"

//JWT handling:
import { jwtDecode } from "jwt-decode"

const nonJwtKeys = ["googlePlacesKey", "mapBoxKey"]
const INITIAL_MAIN_TOKEN_MAX_RETRIES = 3
const INITIAL_MAIN_TOKEN_RETRY_DELAY_MS = 30000
// Hard cap for the initial /token exchange so bootstrap can retry instead of hanging indefinitely.
const INITIAL_MAIN_TOKEN_REQUEST_TIMEOUT_MS = 30000

export async function getUserToken(authUser, options = {}) {
  // Optional AbortSignal allows callers to cancel long-running requests on timeout/user switch.
  const { signal } = options

  try {
    // Send the Firebase ID token to verify identity and obtain an app session token.
    const firebaseToken = await authUser.getIdToken()
    const getTokenUrl = `${serverUrl}/token`

    const getToken = await axios.post(
      getTokenUrl,
      {
        staff_id: authUser.uid,
      },
      {
        headers: {
          Authorization: `Bearer ${firebaseToken}`,
        },
        signal,
      },
    )

    const newToken = getToken.data.token || null

    return newToken
  } catch (error) {
    LogError("Error fetching user token:", error)
    return null
  }
}

/**
 * A custom hook to manage authentication tokens lifecycle, including refreshing tokens and handling expiration.
 */

export default function useTokenManager(userProfile, onMainTokenRefresh) {
  const [initialTokenFetchDone, setInitialTokenFetchDone] = useState(false)
  const [initialTokenFetchError, setInitialTokenFetchError] = useState(null)
  const [fetchingServiceTokens, setFetchingServiceTokens] = useState(true)
  const [hasMainToken, setHasMainToken] = useState(false)
  const mainTokenRequestIdRef = useRef(0)
  const initialMainTokenRetryCountRef = useRef(0)
  const [initialMainTokenRetryTick, setInitialMainTokenRetryTick] = useState(0)

  const tokens = useRef({
    main: null,
    notification: null,
    task: null,
    documentSign: null,
    appointments: null,
    housing: null,
    telerecovery: null,
    employment: null,
    assessments: null,
    gpra: null,
    coordinationBoards: null,
    kiosk: null,
    googlePlacesKey: null,
    mapBoxKey: null,
  })

  const [refreshTokens] = useLazyQuery(GET_MICROSERVICE_TOKENS, {
    fetchPolicy: "network-only",
  })

  //Process service tokens and set up refresh on mount
  const fetchServiceTokens = useCallback(async () => {
    try {
      setFetchingServiceTokens(true)
      const { data } = await refreshTokens()
      tokens.current = {
        ...tokens.current,
        notification: data?.notifications?.jwt,
        task: data?.tasks?.jwt,
        documentSign: data?.documentSign?.jwt,
        appointments: data?.appointments?.jwt,
        housing: data?.housing?.jwt,
        telerecovery: data?.telerecovery?.jwt,
        employment: data?.employment?.jwt,
        assessments: data?.assessments?.jwt,
        gpra: data?.gpra?.jwt,
        coordinationBoards: data?.coordinationBoards?.jwt,
        kiosk: data?.kiosk?.jwt,
        googlePlacesKey: data?.googlePlaces?.data,
        mapBoxKey: data?.mapBox?.data,
      }
    } catch (error) {
      LogError("Error fetching service tokens:", error)
    } finally {
      setFetchingServiceTokens(false)
      setInitialTokenFetchDone(true)
    }
  }, [refreshTokens])

  // Dedicated refresh for main token
  const fetchMainToken = useCallback(async () => {
    if (!userProfile) return

    const auth = getAuth()
    const currentUser = auth.currentUser
    if (!currentUser) return

    const requestId = mainTokenRequestIdRef.current + 1
    mainTokenRequestIdRef.current = requestId
    const requestUserUid = currentUser.uid
    // Per-attempt controller lets us cancel hung HTTP requests when deadline expires.
    const requestAbortController = new AbortController()
    let requestDeadlineId

    const isStaleMainTokenRequest = () => {
      const activeUser = auth.currentUser

      // Ignore completions from superseded attempts or from a different signed-in user.
      if (mainTokenRequestIdRef.current !== requestId) return true
      if (!activeUser || activeUser.uid !== requestUserUid) return true
      if (userProfile?.uid && userProfile.uid !== requestUserUid) return true

      return false
    }

    try {
      // Race token fetch against a deadline so retries can progress even when transport stalls.
      const newMainToken = await Promise.race([
        getUserToken(currentUser, {
          signal: requestAbortController.signal,
        }),
        new Promise((_, reject) => {
          requestDeadlineId = setTimeout(() => {
            LogError("Initial main token request timed out", {
              timeoutMs: INITIAL_MAIN_TOKEN_REQUEST_TIMEOUT_MS,
              requestId,
              uid: requestUserUid,
            })

            // Invalidate this attempt before retrying, then cancel in-flight HTTP work.
            if (mainTokenRequestIdRef.current === requestId) {
              mainTokenRequestIdRef.current += 1
            }
            requestAbortController.abort()
            reject(new Error("Initial main token request timed out"))
          }, INITIAL_MAIN_TOKEN_REQUEST_TIMEOUT_MS)
        }),
      ])

      if (requestDeadlineId) clearTimeout(requestDeadlineId)
      if (isStaleMainTokenRequest()) return

      if (newMainToken) {
        tokens.current = {
          ...tokens.current,
          main: newMainToken,
        }
        initialMainTokenRetryCountRef.current = 0
        setInitialTokenFetchError(null)
        setHasMainToken(true)

        if (onMainTokenRefresh) {
          onMainTokenRefresh(newMainToken) // Inform parent component of the new main token
        }
      } else {
        setHasMainToken(false)
      }
    } catch (error) {
      if (requestDeadlineId) clearTimeout(requestDeadlineId)
      if (isStaleMainTokenRequest()) return
      LogError("Error refreshing main token:", error)
      setHasMainToken(false)
    }
  }, [onMainTokenRefresh, userProfile])

  useEffect(() => {
    if (userProfile) return

    mainTokenRequestIdRef.current += 1

    tokens.current = {
      ...tokens.current,
      main: null,
    }
    initialMainTokenRetryCountRef.current = 0
    setInitialTokenFetchError(null)
    setInitialTokenFetchDone(false)
    setHasMainToken(false)
  }, [userProfile])

  const retryInitialTokenFetch = useCallback(() => {
    initialMainTokenRetryCountRef.current = 0
    setInitialTokenFetchError(null)
    setInitialTokenFetchDone(false)
    setInitialMainTokenRetryTick((current) => current + 1)
  }, [])

  useEffect(() => {
    if (!hasMainToken) return
    fetchServiceTokens()
  }, [fetchServiceTokens, hasMainToken])

  // Dedicated effect for main token refresh
  useEffect(() => {
    if (!userProfile) return

    const bufferMs = 5 * 60 * 1000 // 5 minutes
    let timeoutId
    let isMounted = true

    const scheduleRefresh = async () => {
      const mainToken = tokens.current.main

      if (!isMounted) return

      if (!mainToken) {
        await fetchMainToken()
      }

      const refreshedMainToken = tokens.current.main

      if (!refreshedMainToken) {
        // Timeouts/errors count as failed attempts; after cap we stop loader-only state.
        initialMainTokenRetryCountRef.current += 1

        if (
          initialMainTokenRetryCountRef.current >= INITIAL_MAIN_TOKEN_MAX_RETRIES
        ) {
          setInitialTokenFetchError(
            "We could not start your session. Please try again.",
          )
          setInitialTokenFetchDone(true)
          return
        }

        timeoutId = setTimeout(
          scheduleRefresh,
          INITIAL_MAIN_TOKEN_RETRY_DELAY_MS,
        )
        return
      }

      initialMainTokenRetryCountRef.current = 0
      setInitialTokenFetchError(null)

      let exp = null

      try {
        const payload = jwtDecode(refreshedMainToken)
        exp = payload.exp ? payload.exp * 1000 : null
      } catch {
        exp = null
      }

      if (!exp) return

      const delay = exp - Date.now() - bufferMs

      if (delay <= 0) {
        const now = Date.now()
        const lastRefresh = tokens.current.lastMainRefreshTime || 0

        //Ensure that we always wait at least 30 seconds between refresh attempts to prevent infinite loops if something goes wrong
        if (now - lastRefresh < 30000) {
          timeoutId = setTimeout(scheduleRefresh, 30000 - (now - lastRefresh))
          return
        }

        tokens.current.lastMainRefreshTime = now

        await fetchMainToken()
        scheduleRefresh()
      } else {
        timeoutId = setTimeout(scheduleRefresh, delay)
      }
    }

    scheduleRefresh()
    return () => {      
      isMounted = false
      if (timeoutId) clearTimeout(timeoutId)
    }
  }, [userProfile, fetchMainToken, initialMainTokenRetryTick])

  // Effect to schedule service token refreshes based on their expiries
  useEffect(() => {
    if (!hasMainToken) return
    if (fetchingServiceTokens) return // Don't run until fetch is done

    const bufferMs = 5 * 60 * 1000 // 5 minutes

    //Retrieve the expiries for each service token, filter out any that are missing or invalid
    const expiries = Object.entries(tokens.current)
      .filter(([key]) => key !== "main" && !nonJwtKeys.includes(key)) // Ignore non-jwt keys and main token
      .map(([_, token]) => {
        if (!token) return null
        try {
          const payload = jwtDecode(token)
          // exp is in seconds, convert to ms
          return payload.exp ? payload.exp * 1000 : null
        } catch {
          return null
        }
      })
      .filter(Boolean) //Remove falsey values (nulls from missing/invalid tokens or non-jwt keys)

    if (expiries.length === 0) return

    //Find the soonest expiry and set a timeout to refresh tokens shortly before that
    const soonestExpiry = Math.min(...expiries)
    const delay = soonestExpiry - Date.now() - bufferMs
    const nextDelay = delay > 0 ? delay : 30000 // Minimum 30 seconds delay if expired/expiring to prevent infinite loop

    const timeoutId = setTimeout(() => {
      fetchServiceTokens()
    }, nextDelay)

    return () => clearTimeout(timeoutId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchingServiceTokens, hasMainToken])

  const getMainToken = useCallback(() => tokens.current.main, [])
  const getNotificationToken = useCallback(() => tokens.current.notification, [])
  const getTaskToken = useCallback(() => tokens.current.task, [])
  const getDocumentSignToken = useCallback(() => tokens.current.documentSign, [])
  const getAppointmentsToken = useCallback(() => tokens.current.appointments, [])
  const getHousingToken = useCallback(() => tokens.current.housing, [])
  const getTelerecoveryToken = useCallback(() => tokens.current.telerecovery, [])
  const getEmploymentToken = useCallback(() => tokens.current.employment, [])
  const getAssessmentsToken = useCallback(() => tokens.current.assessments, [])
  const getGpraToken = useCallback(() => tokens.current.gpra, [])
  const getCoordinationBoardsToken = useCallback(() => tokens.current.coordinationBoards, [])
  const getKioskToken = useCallback(() => tokens.current.kiosk, [])
  const getGooglePlacesKey = useCallback(() => tokens.current.googlePlacesKey, [])
  const getMapBoxKey = useCallback(() => tokens.current.mapBoxKey, [])

  return {
    initialTokenFetchDone,
    initialTokenFetchError,
    retryInitialTokenFetch,
    hasMainToken,
    getMainToken,
    getNotificationToken,
    getTaskToken,
    getDocumentSignToken,
    getAppointmentsToken,
    getHousingToken,
    getTelerecoveryToken,
    getEmploymentToken,
    getAssessmentsToken,
    getGpraToken,
    getCoordinationBoardsToken,
    getKioskToken,
    getGooglePlacesKey,
    getMapBoxKey,
  }
}
